import { Box, Button } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import useAPI from "../../api/hooks/useAPI";
import { useCheckUserPermission } from "../../api/hooks/useCheckUserPermission";
import useColors from "../../api/hooks/useColors";
import {
  controlKeys,
  remediationAuditLogKeys,
  remediationKeys,
} from "../../api/queryKeys";
import SvgToIcon from "../../icons/SvgToIcon";
import { icons } from "../../scenes/stack/environment/icons/iconExport";
import {
  PLATFORM_USER_ACTIONS,
  VIEWER_ACCESS_DENIED_MESSAGE,
} from "../../utils/constants";
import CustomToolTipWithPermission from "../CustomToolTipWithPermission";
import ConfirmationDialog from "../dialogs/ConfirmationDialog";
import { Toast } from "../Toast";
export default function DeleteAtlassianApiToken({
  controlId,
  apiTokens = [],
  showToolTip = true,
  disabled = false,
  ...props
}) {
  const colors = useColors();
  const queryClient = useQueryClient();
  const { deleteAtlassianApiToken, trackEvent } = useAPI();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { permitted: hasPermission } = useCheckUserPermission(
    PLATFORM_USER_ACTIONS.DELETE_ATLASSIAN_API_TOKEN
  );

  const handleDelete = async () => {
    trackEvent("User clicked on 'Delete Atlassian API token' button");
    setIsDialogOpen(true);
  };

  const handleRevokeConfirmButton = async () => {
    trackEvent("User confirmed Atlassian API token deletion");
    setIsLoading(true);

    try {
      if (apiTokens.length > 0) {
        const result = await deleteAtlassianApiToken(controlId, apiTokens);
        const { success = [], failed = [] } = result?.data || {};
        const total = success.length + failed.length;
        const succeeded = success.length;
        const failedCount = failed.length;

        if (succeeded === total) {
          Toast.success(
            `Successfully deleted ${succeeded}/${total} API tokens`
          );
        } else if (succeeded > 0) {
          Toast.info(
            `Deleted ${succeeded}/${total} API tokens. Failed for ${failedCount}.`
          );
        } else {
          Toast.error("Unable to delete Atlassian API token.");
        }

        // Refresh the misconfigurations table and related queries
        queryClient.invalidateQueries({ queryKey: controlKeys.all });
        queryClient.invalidateQueries({
          queryKey: remediationAuditLogKeys.auditLogs(controlId),
        });
        queryClient.invalidateQueries({ queryKey: remediationKeys.all });
      }
    } catch (error) {
      Toast.error("Failed to delete Atlassian API token");
    } finally {
      setIsLoading(false);
      handleCloseDialog();
    }
  };

  const handleCloseDialog = () => {
    if (!isLoading) {
      setIsDialogOpen(false);
    }
  };

  return (
    <>
      <CustomToolTipWithPermission
        title={
          hasPermission
            ? "Delete Atlassian API token"
            : VIEWER_ACCESS_DENIED_MESSAGE
        }
        permission={hasPermission}
        placement="top"
      >
        <Box>
          <Button
            sx={{
              color: colors.text_primary,
              backgroundColor: colors.common_white,
              width: "32px",
              height: "32px",
              minWidth: "auto",
              border: "1px solid " + colors.enabled_stroke,
              textTransform: "unset",
              borderRadius: "8px",
              cursor: "pointer",
              ...props.sx,
            }}
            onClick={handleDelete}
            disabled={disabled || !hasPermission}
          >
            <SvgToIcon
              IconLogo={icons.RevokeTokenIcon}
              style={{
                width: "18px",
                color:
                  disabled || !hasPermission
                    ? colors.action_disabled
                    : colors.icon_action_active,
              }}
            />
          </Button>
        </Box>
      </CustomToolTipWithPermission>

      {/* Confirmation dialog */}
      <ConfirmationDialog
        openDialog={isDialogOpen}
        handleDialogClose={handleCloseDialog}
        handleConfirm={handleRevokeConfirmButton}
        dialogTitle="Delete Atlassian API token"
        dialogContentFirstLine="Are you sure you want to delete the Atlassian API token? This action cannot be undone."
        dialogContentSecondLine="The API token will be deleted from the specified Atlassian applications."
        confirmationButtonText="Delete"
        destructive={true}
        isLoading={isLoading}
      />
    </>
  );
}
